import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import ToolTour, { useToolTour, openTour, InfoTip, MarkerKey, ProfitSketch, RecommendedFilters, AdvancedNote } from '../components/ToolTour'
import MiddlesCard from './MiddlesCard'

const C = {
  border: '#1b2538',
  borderStrong: '#26354f',
  text: '#f1f5f9',
  muted: '#94a3b8',
  dim: '#64748b',
  green: '#34d399',
  blue: '#60a5fa',
  red: '#f87171',
}

// Education's worked example (lesson 5.3): total points in Adelaide v Gold
// Coast, carried through slides 1, 2 and 4
const OVER = { book: 'Ladbrokes', pick: 'Over 131.5', line: 131.5, odds: 1.22, stake: 163.69 }
const UNDER = { book: 'Sportsbet', pick: 'Under 133.5', line: 133.5, odds: 5.5, stake: 36.31 }
const QL = 0.30      // lost if the middle misses
const HIT = 199.41   // won if it lands on 132 or 133
const EFFECTIVE = 670

const panel = {
  boxSizing: 'border-box', borderRadius: '14px',
  background: 'linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)', border: `1px solid ${C.borderStrong}`,
}
const label = { fontSize: '10px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em' }

// Slide 1 visual: the two lines on a number line, with the gap where both win
function GapLine({ isMobile }) {
  const lo = 128.5, hi = 136.5
  const x = (v) => `${((v - lo) / (hi - lo)) * 100}%`
  const barH = isMobile ? 26 : 30
  const bar = (side, from, to, tint, align) => (
    <div className="tt-rise" style={{
      position: 'absolute', left: x(from), width: `calc(${x(to)} - ${x(from)})`, height: barH, boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', justifyContent: align, gap: '6px', padding: '0 8px',
      borderRadius: '8px', backgroundColor: `rgba(${tint},0.14)`, border: `1px solid rgba(${tint},0.4)`,
      animationDelay: side === OVER ? '0.15s' : '0s',
    }}>
      <img src={BOOKMAKER_LOGOS[side.book]} alt="" style={{ width: isMobile ? 16 : 18, height: isMobile ? 16 : 18, borderRadius: '4px', objectFit: 'cover' }} />
      <span style={{ fontSize: isMobile ? '11.5px' : '12.5px', fontWeight: 700, color: C.text, whiteSpace: 'nowrap' }}>{side.pick}</span>
      <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600, color: C.muted }}>{side.odds.toFixed(2)}</span>
    </div>
  )
  const ticks = []
  for (let v = 129; v <= 136; v++) ticks.push(v)

  return (
    <div style={{ width: '100%', maxWidth: isMobile ? 'none' : '440px' }}>
      <div style={{ ...label, textAlign: 'center', marginBottom: isMobile ? '6px' : '8px' }}>Total points · Adelaide v Gold Coast</div>
      <div style={{ position: 'relative', height: barH * 2 + 6 + 22 }}>
        {/* the gap, behind both bars */}
        <div className="tt-fade" style={{
          position: 'absolute', top: -4, bottom: 18, left: x(OVER.line), width: `calc(${x(UNDER.line)} - ${x(OVER.line)})`,
          borderRadius: '8px', backgroundColor: 'rgba(16,185,129,0.10)', border: '1px dashed rgba(52,211,153,0.6)',
          animationDelay: '0.45s',
        }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: barH }}>{bar(UNDER, lo, UNDER.line, '96,165,250', 'flex-start')}</div>
        <div style={{ position: 'absolute', top: barH + 6, left: 0, right: 0, height: barH }}>{bar(OVER, OVER.line, hi, '167,139,250', 'flex-end')}</div>
        {ticks.map(v => (
          <span key={v} style={{
            position: 'absolute', bottom: 0, left: x(v), transform: 'translateX(-50%)',
            fontSize: isMobile ? '10.5px' : '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
            color: v > OVER.line && v < UNDER.line ? C.green : C.dim,
          }}>{v}</span>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '8px' : '10px', marginTop: isMobile ? '10px' : '12px' }}>
        <div className="tt-rise" style={{ ...panel, padding: isMobile ? '8px 10px' : '9px 14px', animationDelay: '0.6s' }}>
          <div style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600, color: C.muted }}>Misses, most of the time</div>
          <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: 800, color: C.red, marginTop: '2px' }}>-${QL.toFixed(2)}</div>
        </div>
        <div className="tt-rise" style={{ ...panel, padding: isMobile ? '8px 10px' : '9px 14px', border: '1px solid rgba(16,185,129,0.35)', backgroundColor: 'rgba(16,185,129,0.08)', animationDelay: '0.75s' }}>
          <div style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600, color: C.muted }}>Lands on 132 or 133</div>
          <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: 800, color: C.green, marginTop: '2px' }}>+${HIT.toFixed(2)}</div>
        </div>
      </div>
    </div>
  )
}

// Slide 2 visual: effective odds = the win over the loss
function EffectiveOdds({ isMobile }) {
  const tile = (title, value, color, extra, delay) => (
    <div className="tt-rise" style={{ ...panel, flex: 1, minWidth: 0, padding: isMobile ? '10px' : '12px 14px', textAlign: 'center', animationDelay: `${delay}s`, ...extra }}>
      <div style={label}>{title}</div>
      <div style={{ fontSize: isMobile ? '19px' : '23px', fontWeight: 800, color, marginTop: '4px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  )
  const op = (sign, delay) => (
    <span className="tt-fade" style={{ flexShrink: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: C.dim, animationDelay: `${delay}s` }}>{sign}</span>
  )
  return (
    <div style={{ width: '100%', maxWidth: isMobile ? 'none' : '480px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px' }}>
        {tile('If it hits', `+$${HIT.toFixed(0)}`, C.green, null, 0)}
        {op('÷', 0.2)}
        {tile('If it misses', `$${QL.toFixed(2)}`, C.red, null, 0.3)}
        {op('=', 0.5)}
        {tile('Effective odds', `${EFFECTIVE}`, C.blue, { border: '1px solid rgba(96,165,250,0.45)', boxShadow: '0 0 24px rgba(59,130,246,0.2)' }, 0.6)}
      </div>
      <div className="tt-fade" style={{ marginTop: isMobile ? '12px' : '16px', textAlign: 'center', fontSize: isMobile ? '12px' : '13px', fontWeight: 600, color: C.muted, animationDelay: '0.9s' }}>
        Paid <span style={{ color: C.text, fontWeight: 800 }}>{EFFECTIVE} to 1</span> on the total landing in a 2 point window
      </div>
    </div>
  )
}

// Slide 3. Total stake sits bottom right so its (i) opens inside the card.
function TotalStakeInfo() {
  return (
    <InfoTip label="What total stake means" title="Total stake" align="end">
      What you spend on each middle in total. It's split between the two bookies so a miss costs the same small amount whichever side wins, and the card shows each side's share.
    </InfoTip>
  )
}

const RECOMMENDED = [
  { label: 'Sports', value: 'All' },
  { label: 'Bookmakers', value: 'Your accounts' },
  { label: 'QL %', value: '-2% or better' },
  { label: 'Starts in', value: '0 to 24 hrs' },
  { label: 'Rounding', value: '$5' },
  { label: 'Total stake', value: 'To suit you', info: <TotalStakeInfo /> },
]

// Slide 4: the real card, carrying the same middle
function QlInfo() {
  return (
    <InfoTip label="What QL means" title="Qualifying loss (QL)">
      What a miss costs you, as a share of your total stake. -0.15% on ${(OVER.stake + UNDER.stake).toFixed(0)} is {Math.round(QL * 100)} cents.
    </InfoTip>
  )
}

function OddsMovedInfo() {
  return (
    <InfoTip label="If the odds have moved" title="Odds moved?">
      Tap the arrow on the card to open the calculator and enter the new odds, to check the middle is still worth taking.
    </InfoTip>
  )
}

function CardGuide({ isMobile }) {
  // Commence times are naive UTC, the same as the API sends them
  const commence = new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 19)
  const opp = {
    sport: 'AFL', match: 'Adelaide Crows vs Gold Coast Suns', market_type: 'alternate_total_points',
    commence_time: commence, ql: -0.15,
    outcomes: [OVER, UNDER].map(s => ({ bookmaker: s.book, selection: s.pick, odds: s.odds })),
  }
  return (
    // zoom (not transform) so the card's layout box shrinks too and the key
    // below stays in the stage. Two outcome rows make this card taller than
    // the single-pick tools', hence the smaller zoom.
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: isMobile ? '236px' : '320px' }}>
        <div style={{ zoom: isMobile ? 0.68 : 0.78 }}>
          <MiddlesCard
            opp={opp}
            className="tt-card"
            oddsText={[OVER, UNDER].map(s => s.odds.toFixed(2))}
            stakes={[OVER, UNDER].map(s => s.stake.toFixed(2))}
            markers={{ market: 1, books: 2, outcomes: 3, odds: 4, stakes: 5, ql: 6, track: 7 }}
          />
        </div>
      </div>
      <MarkerKey isMobile={isMobile} labels={['Market', 'Bookmaker', 'Outcome', 'Odds', 'Stake', <>QL <QlInfo /></>, 'Track']} />
    </div>
  )
}

// Runs of small misses, each ended by a hit that pays for them all at once
const MIDDLE_RUN = [0, -0.6, -1.2, -1.8, -2.4, -3, -3.6, -4.2, -4.8, -5.4, -6, 10, 9.4, 8.8, 8.2, 7.6, 7, 6.4, 5.8, 5.2, 4.6,
  20.6, 20, 19.4, 18.8, 18.2, 17.6, 17, 16.4, 15.8, 33]

function middlesTourSlides(isMobile) {
  return [
    {
      title: 'How middles work',
      body: (
        <>
          A middle is a bet on both sides of the same market, at two bookies with different lines. Usually one side wins and you lose a little, called the qualifying loss (QL). But if the result lands in the gap between the lines, both bets win for a big profit.
          <AdvancedNote>Our highest variance strategy. Most middles miss, and you can go long stretches between hits. New to this? Start with the regular EV tool or arbitrage first.</AdvancedNote>
        </>
      ),
      visual: <GapLine isMobile={isMobile} />,
    },
    {
      title: 'Effective odds',
      body: `Effective odds compare the win to the loss: what you make if it hits, divided by what you lose if it misses. Here that's about ${EFFECTIVE}. When the real chance of the result landing in the gap is better than that, the middle makes money over time. The page works this out for every middle.`,
      visual: <EffectiveOdds isMobile={isMobile} />,
    },
    {
      title: 'Set your filters',
      body: 'Here\'s our recommended setup. Keep the QL small, so each miss costs you little while you wait for a hit. Once it\'s set, tap Save Current and set it as your default so it loads every time.',
      visual: <RecommendedFilters rows={RECOMMENDED} isMobile={isMobile} />,
    },
    {
      title: 'Using the middles page',
      body: (
        <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '4px' }}>
          <li>Tap each bookie logo to open the game.</li>
          <li>Fill out both bet slips with the stakes shown, but don't confirm yet.</li>
          <li>Check the odds still match <OddsMovedInfo />, then place both straight after one another.</li>
          <li>Tap <strong style={{ color: C.text }}>+</strong> to track it in your Tracker.</li>
        </ol>
      ),
      visual: <CardGuide isMobile={isMobile} />,
    },
    {
      title: 'You\'re ready to go',
      body: 'Middles disappear fast, so act quickly. Most will miss for a small loss, then one lands and pays for many misses at once. You can go long stretches without a hit, so judge it over hundreds of middles, not days.',
      visual: <ProfitSketch isMobile={isMobile} points={MIDDLE_RUN} headline="Small misses, big hits" sub="Judge it over hundreds of middles" />,
    },
  ]
}

export const openMiddlesTour = () => openTour('middles')

export default function MiddlesTour({ isVisible, isMobile }) {
  const { hasAccess } = useAuth()
  const tour = useToolTour('middles', { isVisible })
  return (
    <ToolTour
      open={tour.open}
      onClose={tour.close}
      slides={middlesTourSlides(isMobile)}
      eyebrow="Middles"
      isMobile={isMobile}
      finalAction={hasAccess('middles') ? { label: 'Get into it' } : { label: 'Unlock with Sports Premium', onClick: () => { window.location.hash = '#/pricing' } }}
      learnMore={{ label: 'Learn more', onClick: () => window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'education' })) }}
    />
  )
}
